import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { currencyFieldToNumber, isInvalid } from '../../helpers/functions';
import CurrencyInput from '../../Components/Common/CurrencyInput';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as moment from 'moment';
import { useProfile } from '../../Components/Hooks/UserHooks';
import { createOrder, sendToken, updateCustomer } from '../../slices/thunks';
import TwoFactor from './TwoFactor';
import { toNumber, isEmpty } from 'lodash';
import { formatDate } from '../../helpers/format';
import { IMaskInput } from 'react-imask';
import classnames from 'classnames';

const SecondTab = ({
  t,
  onSubmit,
  customer,
  wallet,
  reset,
  order,
  setRescueBalance,
  rescueBalance,
  simulateOrderIsLoading,
}) => {
  const { userProfile } = useProfile();
  const dispatch = useDispatch();

  const [useBalance, setUseBalance] = useState(true);
  const [shouldSendToken, setShouldSendToken] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorModal, setTwoFactorModal] = useState(false);
  const [disabledFinishOrder, setDisabledFinishOrder] = useState(false);

  useEffect(() => {
    if (reset) {
      validation.resetForm();
      setIsLoading(false);
      setTwoFactorModal(false);
      setUseBalance(true);
      setShouldSendToken(true);
      setDisabledFinishOrder(false);
    }

    if (
      order?.cashback?.maximumRescue &&
      order?.cashback?.maximumRescue > 0
    ) {
      setUseBalance(true);
    } else {
      setUseBalance(false);
    }
  }, [reset, order?.cashback?.maximumRescue]);

  const onCloseTwoFactor = () => {
    setIsLoading(false);
  };

  const submit = async (values) => {
    setIsLoading(true);
    const rescue_balance = currencyFieldToNumber(values.rescue_balance);
    const customerDocument = order?.document;

    if (rescue_balance > values.rescue_limit) {
      validation.setFieldError(
        'rescue_balance',
        t('validation-max-rescue-balance')
      );
      setIsLoading(false);
      setDisabledFinishOrder(false);
      return;
    }

    const ticket = `${moment().unix()}-${customerDocument}`;

    const payload = {
      document: customerDocument,
      ticket,
      seller: userProfile.name,
      store: userProfile?.stores?.[0]?.name ?? '',
      storeId: userProfile?.stores?.[0]?.id ?? '',
      status: 'paid',
      total: order?.total,
      totalPaid: useBalance ? order?.total - rescue_balance : order?.total,
      usedCashback: useBalance ? rescue_balance : 0,
      master_token_used: values.master_token_used,
    };

    try {
      const result = await dispatch(
        createOrder({
          order: payload,
          errorMessage: t('new-order-error'),
        })
      );

      if (result?.payload?.error) {
        setIsLoading(false);
        setDisabledFinishOrder(false);
        return;
      }

      setIsLoading(false);
      validation.resetForm();
      onSubmit(result.payload, userProfile);
    } catch (error) {
      setIsLoading(false);
      setDisabledFinishOrder(false);
    }
  };

  const toggleTwoFactorModal = () => {
    setTwoFactorModal(!twoFactorModal);
  };

  const twoFactorSuccess = async (payload) => {
    const { is_master_token } = payload;
    setTwoFactorModal(false);
    setDisabledFinishOrder(true);
    await submit({ ...validation.values, master_token_used: is_master_token });
  };

  const handleRescueBalanceChange = (e) => {
    const value = Math.min(
      currencyFieldToNumber(e),
      toNumber(validation.values.rescue_limit)
    );
    validation.setFieldValue('rescue_balance', value);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      cellphone: (wallet && wallet.cellphone) || '',
      email: (wallet && wallet.email) || '',
      rescue: true,
      rescue_balance:
        order?.cashback?.maximumRescue && rescueBalance > 0
          ? Math.min(rescueBalance, order?.cashback?.maximumRescue)
          : order?.cashback?.maximumRescue || '0',
      rescue_limit: order?.cashback?.maximumRescue || '0',
    },
    validationSchema: Yup.object({
      cellphone: Yup.string().required(t('validation-required-cellphone')),
      email: Yup.string().email(t('validation-valid-email')),
      rescue: Yup.boolean().required(t('validation-required-use-rescue')),
      rescue_balance: Yup.string().when('rescue', {
        is: true,
        then: () =>
          Yup.string().required(t('validation-required-rescue-balance')),
      }),
    }),
    onSubmit: async (values) => {
      setDisabledFinishOrder(true);
      setIsLoading(true);

      const rescue_balance = currencyFieldToNumber(values.rescue_balance);

      if (rescue_balance > values.rescue_limit) {
        validation.setFieldError(
          'rescue_balance',
          t('validation-max-rescue-balance')
        );
        setIsLoading(false);
        setDisabledFinishOrder(false);
        return;
      }

      const customerDocument = order?.document;

      if (
        values.cellphone !== wallet.cellphone ||
        values.email !== wallet.email
      ) {
        const customer_payload = {
          email: !isEmpty(values.email) ? values.email : undefined,
          cellphone: values.cellphone,
        };

        await dispatch(
          updateCustomer({
            document: customerDocument,
            customer: customer_payload,
          })
        );
      }

      if (
        !useBalance ||
        (useBalance && currencyFieldToNumber(values.rescue_balance) === 0)
      ) {
        return await submit(values);
      }

      toggleTwoFactorModal();
      if (shouldSendToken) {
        const responseToken = await dispatch(
          sendToken({
            document: customerDocument,
            params: {
              channel: 'sms',
              identifier: 'order-ock-light',
            },
          })
        );

        if (!responseToken?.payload?.error) {
          setShouldSendToken(false);
        }
      }

      setDisabledFinishOrder(false);
    },
  });

  const accountSettings = useSelector(
    (state) => state.GeneralSettings.accountSettings
  );

  return (
    <React.Fragment>
      <Form
        className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Row className="justify-content-center py-5">
          <Col xs={12} lg={5}>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <Label
                    htmlFor="customer-document-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('document')}
                  </Label>
                  <IMaskInput
                    mask={
                      accountSettings?.document_type == 'document_br'
                        ? [
                            {
                              mask: '000.000.000-00',
                              maxLength: 11,
                            },
                            {
                              mask: '00.000.000/0000-00',
                              maxLength: 14,
                            },
                          ]
                        : /^[\w\W]*$/
                    }
                    unmask={true}
                    name="customer-document"
                    id="customer-document-field"
                    className="form-control"
                    placeholder={t('document-placeholder')}
                    type="text"
                    disabled
                    value={(customer && customer.document) || ''}
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <Label
                    htmlFor="name-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('name')}
                  </Label>
                  <Input
                    name="document"
                    id="name-field"
                    className="form-control"
                    placeholder={t('name-placeholder')}
                    type="text"
                    disabled
                    value={(customer && customer.name) || ''}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <Label
                    htmlFor="customer-cellphone-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('cellphone')}
                  </Label>
                  <IMaskInput
                    mask="(00) 0 0000-0000"
                    unmask={true}
                    name="cellphone"
                    id="customer-cellphone-field"
                    className={classnames('form-control', {
                      'is-invalid': isInvalid(validation, 'cellphone'),
                    })}
                    placeholder={t('cellphone-placeholder')}
                    onAccept={(value, mask) =>
                      validation.setFieldValue('cellphone', mask.value)
                    }
                    value={validation.values.cellphone || ''}
                    inputRef={(el) => {
                      if (el) {
                        el.addEventListener('blur', validation.handleBlur);
                      }
                    }}
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <Label
                    htmlFor="customer-birthdate-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('birthdate')}
                  </Label>
                  <IMaskInput
                    mask="00/00/0000"
                    unmask={false}
                    name="birthdate"
                    id="customer-birthdate-field"
                    className="form-control"
                    placeholder={t('birthdate-placeholder')}
                    type="text"
                    disabled
                    value={
                      (customer &&
                        customer.birthdate &&
                        formatDate(customer.birthdate)) ||
                      ''
                    }
                    lazy={false}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Label
                    htmlFor="customer-email-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('email')}
                  </Label>
                  <Input
                    name="email"
                    id="customer-email-field"
                    className="form-control"
                    placeholder={t('email-placeholder')}
                    type="text"
                    validate={{
                      required: { value: true },
                    }}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ''}
                    invalid={isInvalid(validation, 'email') ? true : false}
                  />
                  {isInvalid(validation, 'email') && (
                    <FormFeedback type="invalid">
                      {validation.errors.email}
                    </FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </Col>
          <Col lg={1}></Col>
          <Col xs={12} lg={5}>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Label
                    htmlFor="customer-balance-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('balance-in-wallet')}
                  </Label>
                  <div className="input-group">
                    <span className="input-group-text">{t('currency')}</span>
                    <CurrencyInput
                      name="balance"
                      id="customer-balance-field"
                      placeholder="0,00"
                      type="text"
                      value={wallet && wallet.balance}
                      disabled={true}
                      className="form-control"
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <Label
                    htmlFor="customer-document-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('rescue-balance')} ?
                  </Label>
                  <div
                    className="form-check form-switch form-switch-lg"
                    dir="ltr"
                  >
                    <Input
                      type="checkbox"
                      name="rescue"
                      className="form-check-input"
                      id="customSwitchsizelg"
                      checked={useBalance}
                      disabled={
                        simulateOrderIsLoading &&
                        validation.values.rescue_limit <= 0
                      }
                      onChange={(e) => {
                        const useBalanceStatus = !useBalance;

                        if (useBalanceStatus === true) {
                          const newRescueBalance = currencyFieldToNumber(
                            validation.values.rescue_balance
                          );

                          setRescueBalance(newRescueBalance);
                        } else {
                          setRescueBalance(0);
                        }

                        setUseBalance(useBalanceStatus);
                      }}
                    />
                    <Label
                      className="form-check-label"
                      for="customSwitchsizelg"
                    ></Label>
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <Label
                    htmlFor="rescue-balance-field"
                    className="form-label text-muted text-uppercase fw-semibold"
                  >
                    {t('amount-rescue-balance')}
                  </Label>
                  <div className="input-group">
                    <span className="input-group-text">{t('currency')}</span>
                    <CurrencyInput
                      name="rescue_balance"
                      id="rescue-balance-field"
                      placeholder="0,00"
                      onChange={handleRescueBalanceChange}
                      onBlur={(e) => {
                        const value = e.currentTarget.value;
                        const newRescueBalance = currencyFieldToNumber(value);

                        setRescueBalance(newRescueBalance);

                        validation.handleBlur(e);
                      }}
                      value={
                        useBalance ? validation.values.rescue_balance : '0,00'
                      }
                      disabled={simulateOrderIsLoading && !useBalance}
                      invalid={isInvalid(validation, 'rescue_balance')}
                    />
                    {isInvalid(validation, 'rescue_balance') && (
                      <FormFeedback type="invalid">
                        {' '}
                        {validation.errors.rescue_balance}{' '}
                      </FormFeedback>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="d-grid gap-2 mt-3">
                  <Button
                    type="submit"
                    // onClick={(e) => {
                    //   e.preventDefault()
                    //   // toggleTwoFactorModal()
                    // }}
                    className="btn btn-tertiary text-white"
                    disabled={disabledFinishOrder}
                  >
                    {t('order-step')}
                    {isLoading && <Spinner size="sm" className="ms-2" />}
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
      <TwoFactor
        isOpen={twoFactorModal}
        toggle={toggleTwoFactorModal}
        document={order?.document}
        onClose={onCloseTwoFactor}
        onSuccess={twoFactorSuccess}
        reset={reset}
      />
    </React.Fragment>
  );
};

export default withTranslation()(SecondTab);
